import { Empty, Grid, Pagination, Spin, Table } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  pinActionsColumn,
  withTableScroll,
} from '../../utilities/pinActionsColumn';
import '../../styles/listPage.css';

const { useBreakpoint } = Grid;

const ACTIONS_KEYS = new Set(['actions', 'action', 'properties_action']);
const STATUS_KEYS = new Set([
  'status',
  'type',
  'accountStatus',
  'account_status',
  'userType',
  'user_type',
  'is_active',
  'operation',
  'accountRole',
]);
const TITLE_KEYS = new Set(['name', 'title', 'id', 'reference', 'label']);

function getColumnKey(column, index) {
  return column?.key ?? column?.dataIndex ?? `col-${index}`;
}

function getNestedValue(record, dataIndex) {
  if (dataIndex == null) return undefined;
  if (Array.isArray(dataIndex)) {
    return dataIndex.reduce(
      (acc, key) => (acc == null ? acc : acc[key]),
      record,
    );
  }
  return record?.[dataIndex];
}

function renderColumnCell(column, record, index) {
  if (!column) return null;
  const value = getNestedValue(record, column.dataIndex);
  if (typeof column.render === 'function') {
    return column.render(value, record, index);
  }
  if (value == null || value === '') return '—';
  return value;
}

function resolveRowKey(rowKey, record) {
  if (typeof rowKey === 'function') return rowKey(record);
  if (typeof rowKey === 'string' && record?.[rowKey] != null) {
    return record[rowKey];
  }
  return record?.key ?? record?.id;
}

function pickColumn(columns, preferredKey) {
  if (!preferredKey) return null;
  return (
    columns.find(
      (col) =>
        col?.key === preferredKey ||
        col?.dataIndex === preferredKey ||
        (Array.isArray(col?.dataIndex) &&
          col.dataIndex.join('.') === preferredKey),
    ) ?? null
  );
}

/**
 * Ant Design Table on desktop; card list below the `md` breakpoint
 * (same pattern as Orders / Invoices).
 */
const ResponsiveEntityTable = ({
  columns = [],
  dataSource = [],
  loading = false,
  pagination,
  rowKey = 'id',
  scroll,
  className = 'table-wrapper',
  locale,
  rowSelection,
  onChange,
  size,
  bordered,
  sticky,
  mobileTitleKey,
  mobileStatusKey,
  mobileHideKeys = [],
  empty,
}) => {
  const screens = useBreakpoint();
  const isMobileList = screens.md !== true;
  const [localPage, setLocalPage] = useState(1);
  const [localPageSize, setLocalPageSize] = useState(10);
  const generatedRowKeysRef = useRef(new WeakMap());
  const generatedRowKeysCountRef = useRef(0);

  const getStableFallbackRowKey = useCallback((record) => {
    if (record && typeof record === 'object') {
      const existingKey = generatedRowKeysRef.current.get(record);
      if (existingKey != null) return existingKey;

      generatedRowKeysCountRef.current += 1;
      const generatedKey = `generated-row-${generatedRowKeysCountRef.current}`;
      generatedRowKeysRef.current.set(record, generatedKey);
      return generatedKey;
    }

    return `generated-row-primitive-${String(record)}`;
  }, []);

  const getResolvedRowKey = useCallback((record) => {
    const key = resolveRowKey(rowKey, record);
    return key ?? getStableFallbackRowKey(record);
  }, [getStableFallbackRowKey, rowKey]);

  const visibleColumns = useMemo(
    () => (Array.isArray(columns) ? columns.filter(Boolean) : []),
    [columns],
  );

  const hideKeys = useMemo(
    () => new Set(mobileHideKeys),
    [mobileHideKeys],
  );

  const { titleColumn, statusColumn, actionColumn, metaColumns } =
    useMemo(() => {
      const actions =
        visibleColumns.find(
          (col) =>
            ACTIONS_KEYS.has(col?.key) || ACTIONS_KEYS.has(col?.dataIndex),
        ) ?? null;

      const withoutActions = visibleColumns.filter((col) => col !== actions);

      const status =
        pickColumn(withoutActions, mobileStatusKey) ??
        withoutActions.find(
          (col) =>
            STATUS_KEYS.has(col?.key) || STATUS_KEYS.has(col?.dataIndex),
        ) ??
        null;

      const title =
        pickColumn(
          withoutActions.filter((col) => col !== status),
          mobileTitleKey,
        ) ??
        withoutActions.find(
          (col) =>
            col !== status &&
            (TITLE_KEYS.has(col?.key) || TITLE_KEYS.has(col?.dataIndex)),
        ) ??
        withoutActions.find((col) => col !== status) ??
        null;

      const meta = withoutActions.filter((col) => {
        if (col === title || col === status) return false;
        const key = col?.key ?? col?.dataIndex;
        if (key != null && hideKeys.has(key)) return false;
        if (col?.mobileHidden) return false;
        return true;
      });

      return {
        titleColumn: title,
        statusColumn: status,
        actionColumn: actions,
        metaColumns: meta,
      };
    }, [visibleColumns, mobileTitleKey, mobileStatusKey, hideKeys]);

  const rows = Array.isArray(dataSource) ? dataSource : [];

  // Match Ant Design Table: undefined → default client pagination; false → none
  const paginationEnabled = pagination !== false;
  const paginationConfig =
    pagination && typeof pagination === 'object' ? pagination : null;
  const isServerPaginated =
    paginationConfig != null && paginationConfig.total != null;
  const isClientPaginated = paginationEnabled && !isServerPaginated;

  const pageSize = Number(
    paginationConfig?.pageSize ?? (isClientPaginated ? localPageSize : 10),
  ) || 10;
  const current = Number(
    paginationConfig?.current ?? (isClientPaginated ? localPage : 1),
  ) || 1;
  const total = isServerPaginated
    ? Number(paginationConfig.total) || 0
    : isClientPaginated
      ? rows.length
      : 0;

  useEffect(() => {
    if (!isClientPaginated) return;
    const maxPage = Math.max(1, Math.ceil(rows.length / pageSize) || 1);
    if (localPage > maxPage) setLocalPage(maxPage);
  }, [isClientPaginated, rows.length, pageSize, localPage]);

  const displayRows = isClientPaginated
    ? rows.slice((current - 1) * pageSize, current * pageSize)
    : rows;

  const handleMobilePageChange = (page, nextPageSize) => {
    const size = nextPageSize || pageSize;
    if (isClientPaginated && paginationConfig?.current == null) {
      setLocalPage(page);
      setLocalPageSize(size);
    }
    if (typeof paginationConfig?.onChange === 'function') {
      paginationConfig.onChange(page, size);
    }
    if (typeof onChange === 'function') {
      onChange({
        ...(paginationConfig || {}),
        current: page,
        pageSize: size,
      });
    }
  };

  const emptyNode =
    empty ??
    locale?.emptyText ?? (
      <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
    );

  const desktopColumns = useMemo(
    () => pinActionsColumn(visibleColumns),
    [visibleColumns],
  );
  const desktopScroll = useMemo(() => withTableScroll(scroll), [scroll]);

  if (!isMobileList) {
    return (
      <Table
        className={className}
        columns={desktopColumns}
        dataSource={dataSource}
        loading={loading}
        pagination={pagination}
        rowKey={getResolvedRowKey}
        scroll={desktopScroll}
        locale={locale}
        rowSelection={rowSelection}
        onChange={onChange}
        size={size}
        bordered={bordered}
        sticky={sticky}
      />
    );
  }

  return (
    <div className="list-mobile-cards-wrap">
      <Spin spinning={loading}>
        {rows.length === 0 && !loading ? (
          emptyNode
        ) : (
          <div className="list-mobile-cards">
            {displayRows.map((record, index) => {
              const absoluteIndex = isClientPaginated
                ? (current - 1) * pageSize + index
                : index;
              const key = getResolvedRowKey(record);
              const hasActions = Boolean(actionColumn);

              return (
                <article key={key} className="list-mobile-card">
                  <div className="list-mobile-card-top">
                    <div className="list-mobile-card-id">
                      {titleColumn
                        ? renderColumnCell(titleColumn, record, absoluteIndex)
                        : `#${record?.id ?? key}`}
                    </div>
                    {statusColumn ? (
                      <div className="list-mobile-card-status">
                        {renderColumnCell(
                          statusColumn,
                          record,
                          absoluteIndex,
                        )}
                      </div>
                    ) : null}
                  </div>

                  {metaColumns.length > 0 ? (
                    <dl className="list-mobile-card-meta">
                      {metaColumns.map((col, colIndex) => (
                        <div key={getColumnKey(col, colIndex)}>
                          <dt>{col.title}</dt>
                          <dd>
                            {renderColumnCell(col, record, absoluteIndex)}
                          </dd>
                        </div>
                      ))}
                    </dl>
                  ) : null}

                  {hasActions ? (
                    <div className="list-mobile-card-actions">
                      {renderColumnCell(
                        actionColumn,
                        record,
                        absoluteIndex,
                      )}
                    </div>
                  ) : null}
                </article>
              );
            })}
          </div>
        )}
      </Spin>

      {paginationEnabled && total > 0 ? (
        <div className="list-mobile-pagination">
          <Pagination
            size="small"
            current={current}
            pageSize={pageSize}
            total={total}
            showSizeChanger={
              paginationConfig?.showSizeChanger !== false
            }
            showTotal={paginationConfig?.showTotal}
            pageSizeOptions={paginationConfig?.pageSizeOptions}
            onChange={handleMobilePageChange}
          />
        </div>
      ) : null}
    </div>
  );
};

export default ResponsiveEntityTable;
